"use client";

import { useState, useEffect } from "react";
import type { ProductDetail, ProductVariant } from "@/types";
import { ProductImages } from "@/components/product/product-images";
import { ProductVariants } from "@/components/product/product-variants";
import { AddToCartButton } from "@/components/product/add-to-cart-button";
import { FlyToCart } from "@/components/product/fly-to-cart";
import { useCartStore } from "@/store/cart.store";
import { useRecentlyViewedStore } from "@/store/recently-viewed.store";
import { StockNotifyForm } from "@/components/product/stock-notify-form";
import { TrustBadges } from "@/components/product/trust-badges";
import { ShareButton } from "@/components/product/share-button";
import { WishlistButton } from "@/components/product/wishlist-button";
import { cartKey, formatCurrency, mediaUrl } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/use-media-query";
import { api } from "@/lib/api";
import { Eye } from "lucide-react";

interface Props {
  product: ProductDetail;
  staticInfo: React.ReactNode; // server-rendered badges, title, price, description excerpt
  metaInfo?: React.ReactNode;  // marka/kategori rozetleri — sayfanın altında gösterilir
}

export function ProductPageGrid({ product, staticInfo, metaInfo }: Props) {
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | undefined>(
    product.variants.length === 1 ? product.variants[0] : undefined
  );
  const [quantity, setQuantity] = useState(1);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [isFlying, setIsFlying] = useState(false);
  const [flyStartRect, setFlyStartRect] = useState<DOMRect | undefined>(undefined);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const track = useRecentlyViewedStore((s) => s.track);
  const [viewCount, setViewCount] = useState(product.viewCount ?? 0);

  const handleAddToCart = (btnElement: HTMLButtonElement) => {
    setFlyStartRect(btnElement.getBoundingClientRect());
    setIsFlying(true);
  };

  const handleFlyDone = () => {
    setIsFlying(false);
  };

  useEffect(() => {
    const coverImageUrl =
      product.coverImageUrl ??
      product.images.find((i) => i.isCover)?.url ??
      product.images[0]?.url;
    track({
      id: product.id,
      name: product.name,
      slug: product.slug,
      price: product.price,
      coverImageUrl,
      categoryName: product.categoryName,
      brandName: product.brandName,
      compareAtPrice: product.compareAtPrice,
      trackStock: product.trackStock,
      stock: product.stock,
      lowStockThreshold: product.lowStockThreshold,
      isFeatured: product.isFeatured,
      isOnSale: product.isOnSale,
      discountPercentage: product.discountPercentage,
      dealerPrice: product.dealerPrice,
      variants: product.variants.map((v) => ({ id: v.id, name: v.name, price: v.price ?? 0, stock: v.stock })),
    });
  }, [product.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Görüntülenme sayacı: oturum başına ürün başına bir kez artır (yenilemede şişmesin)
  useEffect(() => {
    const key = `viewed:${product.slug}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api
      .post(`/api/catalog/products/${product.slug}/view`)
      .then((r) => {
        if (typeof r.data?.viewCount === "number") setViewCount(r.data.viewCount);
      })
      .catch(() => {});
  }, [product.slug]);

  useEffect(() => {
    const handleResize = () => {
      // Eğer viewport height < window height * 0.75 ise klavye açık demektir
      setKeyboardOpen(window.innerHeight < window.outerHeight * 0.75);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const cartItems = useCartStore((s) => s.items);
  const cartQty =
    cartItems.find(
      (i) => cartKey(i.productId, i.variantId) === cartKey(product.id, selectedVariant?.id)
    )?.quantity ?? 0;

  const effectiveStock = selectedVariant?.stock ?? product.stock;
  const remaining = product.trackStock ? Math.max(0, effectiveStock - cartQty) : 99;
  const boundedQuantity = product.trackStock && remaining > 0 ? Math.min(quantity, remaining) : quantity;

  const activeImageUrl = selectedVariant?.imageUrl ?? undefined;
  const canIncrement = !product.trackStock || (remaining > 0 && boundedQuantity < remaining);

  const effectivePrice = selectedVariant?.price ?? product.price;
  const showStickyBar = isMobile && !(product.trackStock && remaining === 0);

  // Mobil sticky sepet barı görünürken sayfanın altına boşluk ekle —
  // footer dahil son içerik barın altında kalmasın (ortada boşluk oluşturmaz).
  useEffect(() => {
    if (!showStickyBar || keyboardOpen) return;
    const previous = document.body.style.paddingBottom;
    document.body.style.paddingBottom = "calc(4.5rem + env(safe-area-inset-bottom))";
    return () => {
      document.body.style.paddingBottom = previous;
    };
  }, [showStickyBar, keyboardOpen]);

  return (
    <>
      <FlyToCart
        isFlying={isFlying}
        startRect={flyStartRect}
        productImageUrl={mediaUrl(product.coverImageUrl ?? product.images[0]?.url)}
        onDone={handleFlyDone}
      />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:gap-12">
        {/* Sol — Görseller */}
        <div className="w-full md:max-w-[480px] md:mr-auto">
          <ProductImages
            images={product.images}
            name={product.name}
            activeUrl={activeImageUrl}
          />
        </div>

        {/* Sağ — Bilgi */}
        <div className="space-y-4 md:space-y-5">
          {staticInfo}

          {/* Varyantlar */}
          {product.variants.length > 0 && (
            <ProductVariants
              variants={product.variants}
              selectedId={selectedVariant?.id}
              onSelect={setSelectedVariant}
            />
          )}

          {/* Stok yok — bildirim formu */}
          {product.trackStock && remaining === 0 && (
            <StockNotifyForm productSlug={product.slug} />
          )}

          {/* Miktar + Sepete Ekle — mobilde gizli (altta sticky bar var) */}
          <div className={`mt-5 items-stretch gap-3 sm:mt-6 sm:items-center sm:gap-4 ${product.trackStock && remaining === 0 ? "hidden" : "hidden md:flex"}`}>
            <div className="flex h-12 w-[112px] shrink-0 items-center rounded-sm border border-gray-200 bg-gray-50 sm:w-[120px]">
              <button
                onClick={() => setQuantity(() => Math.max(1, boundedQuantity - 1))}
                className="flex-1 h-full flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-500 hover:text-black font-semibold text-lg"
                aria-label="Miktarı azalt"
              >
                −
              </button>
              <span className="flex-1 h-full flex items-center justify-center text-sm font-bold bg-white border-x border-gray-200">
                {boundedQuantity}
              </span>
              <button
                onClick={() => setQuantity(() => Math.min(remaining, boundedQuantity + 1))}
                disabled={!canIncrement}
                className="flex-1 h-full flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-500 hover:text-black font-semibold text-lg disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Miktarı artır"
              >
                +
              </button>
            </div>
            <div className="h-12 min-w-0 flex-1">
              <AddToCartButton
                product={product}
                selectedVariant={selectedVariant}
                quantity={boundedQuantity}
                onAdd={handleAddToCart}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:block">
            <TrustBadges />
          </div>

          {/* Üst satır: Paylaş/Favori + Marka/Kategori */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Paylaş + Favori — kendi kutuları var, dış sarmalayıcı yok */}
            <div className="flex items-center gap-2">
              <ShareButton title={product.name} slug={product.slug} className="h-11 px-3.5" />
              <WishlistButton
                productId={product.id}
                name={product.name}
                slug={product.slug}
                price={product.price}
                coverImageUrl={product.coverImageUrl}
                size="md"
              />
            </div>

            {/* Marka + Kategori */}
            {metaInfo}
          </div>

          {/* Alt satır: Görüntülenme + Stok uyarısı — yarı yarıya (mobilde alt alta) */}
          {(viewCount > 0 || (product.trackStock && effectiveStock > 0 && effectiveStock <= 10)) && (
            <div className="flex flex-col gap-3 sm:flex-row">
              {/* Görüntülenme — renkli kart */}
              {viewCount > 0 && (
                <span className="flex h-11 items-center justify-center gap-1.5 rounded-[var(--sf-radius)] border border-[color-mix(in_srgb,var(--sf-accent)_45%,transparent)] bg-[color-mix(in_srgb,var(--sf-accent)_10%,transparent)] px-3.5 text-xs font-semibold sm:w-1/4 sm:shrink-0" style={{ color: "var(--sf-accent)" }}>
                  <Eye className="h-4 w-4" />
                  <span className="tabular-nums">{viewCount.toLocaleString("tr-TR")}</span>
                  kişi görüntüledi
                </span>
              )}

              {/* Stok uyarısı */}
              {product.trackStock && effectiveStock > 0 && effectiveStock <= 10 && (
                <div className="flex h-11 items-center gap-2 rounded-[var(--sf-radius)] border border-[var(--sf-badge-low-stock-border)] bg-[var(--sf-badge-low-stock-bg)] px-3 text-sm font-medium sm:flex-1" style={{ color: "var(--sf-badge-low-stock-text)" }}>
                  <svg className="h-4 w-4 shrink-0 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Son {effectiveStock} adet kaldı - Acele edin!
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobilde Sticky Sepete Ekle Barı */}
      {showStickyBar && !keyboardOpen && (
        <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-[var(--es-border)] bg-[var(--es-surface)] px-3 pt-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] shadow-[0_-4px_20px_rgba(0,0,0,0.08)] md:hidden">
          <div className="grid grid-cols-[minmax(74px,0.8fr)_84px_minmax(142px,1.3fr)] items-center gap-2">
            {/* Sol: Ürün adı (kayar yazı) + Fiyat */}
            <div className="min-w-0">
              <div className="overflow-hidden">
                <div className="flex w-max animate-marquee gap-10 whitespace-nowrap">
                  <span className="text-xs text-[var(--es-text-muted)]">{product.name}</span>
                  <span aria-hidden className="text-xs text-[var(--es-text-muted)]">{product.name}</span>
                </div>
              </div>
              <p className="whitespace-nowrap text-base font-bold leading-tight text-[var(--sf-primary)]">
                {formatCurrency(effectivePrice)}
              </p>
            </div>

            {/* Orta: Miktar */}
            <div className="flex h-11 items-center rounded-[var(--sf-radius)] border border-[var(--es-border)] bg-gray-50">
              <button
                onClick={() => setQuantity(() => Math.max(1, boundedQuantity - 1))}
                className="flex h-full flex-1 items-center justify-center px-2 text-lg font-semibold text-gray-500 transition-colors hover:bg-gray-100 hover:text-black"
                aria-label="Miktarı azalt"
              >
                −
              </button>
              <span className="flex h-full w-8 items-center justify-center border-x border-[var(--es-border)] bg-white text-center text-sm font-bold">
                {boundedQuantity}
              </span>
              <button
                onClick={() => setQuantity(() => Math.min(remaining, boundedQuantity + 1))}
                disabled={!canIncrement}
                className="flex h-full flex-1 items-center justify-center px-2 text-lg font-semibold text-gray-500 transition-colors hover:bg-gray-100 hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Miktarı artır"
              >
                +
              </button>
            </div>

            {/* Sağ: Sepete Ekle */}
            <div className="h-11 min-w-0">
              <AddToCartButton
                product={product}
                selectedVariant={selectedVariant}
                quantity={boundedQuantity}
                onAdd={handleAddToCart}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
